import { useEffect, useRef, type ComponentType, type SVGProps } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { useTranslation } from 'react-i18next';
import { Link, matchPath, useLocation, useMatch, useNavigate } from 'react-router-dom';
import { useAuthStore, UserAvatar } from '@entities/User';
import { useNotificationsStore } from '@features/Notifications';
import { SocialRewardSidebarRow } from '@features/SocialReward';
import { paths } from '@shared/config';
import { cn } from '@shared/lib';
import { installedBefore, usePwaInstall } from '@shared/pwa/install';
import {
  BellIcon,
  BookIcon,
  CogIcon,
  GameIcon,
  HomeIcon,
  LightbulbIcon,
  Logo,
  MoreIcon,
  PhoneIcon,
  ShieldIcon,
  SparklesIcon,
  UsersIcon,
  WordsIcon,
} from '@shared/ui';
import { navSectionOf, type NavSection } from './navSections';
import { useSidebarStore } from './sidebarStore';
import { TokenBalance } from './TokenBalance';

type IconCmp = ComponentType<SVGProps<SVGSVGElement>>;

/** Высота панели вкладок без нижнего поля телефона: его панель добавляет сверх. */
export const TAB_BAR_HEIGHT = 60;

/** Низ слоёв над панелью вкладок (лист «Ещё» и его затемнение); `--tabbar-h` задаёт оболочка (Application). */
const ABOVE_TAB_BAR = 'calc(var(--tabbar-h, 0px) + env(safe-area-inset-bottom, 0px))';

interface Tab {
  section: Exclude<NavSection, 'more'>;
  to: string;
  labelKey: string;
  Icon: IconCmp;
  isNew?: boolean;
}

/** Вкладки телефона (решение владельца 2026-10-23): Главная · Слова и модули · Грамматика · Игра · Ещё. Шаги
 *  знакомства — по `data-tour`. */
const TABS: readonly Tab[] = [
  { section: 'home', to: paths.dashboard, labelKey: 'nav.dashboard', Icon: HomeIcon },
  { section: 'words', to: paths.words, labelKey: 'nav.words', Icon: WordsIcon },
  { section: 'grammar', to: paths.grammar, labelKey: 'nav.grammar', Icon: BookIcon },
  { section: 'game', to: paths.stories, labelKey: 'nav.game', Icon: GameIcon, isNew: true },
];

interface MoreItem {
  to: string;
  labelKey: string;
  Icon: IconCmp;
  badge?: number;
}

const TAB_CLASS =
  'relative flex min-w-0 flex-1 flex-col items-center justify-center gap-1 text-center text-[11px] font-medium leading-[13px] tracking-[-0.01em] transition-colors max-[340px]:text-[10.5px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent';

/** Точка на значке вкладки: «новое» у игры, новые уведомления у «Ещё». */
function Dot({ label }: { label?: string }) {
  return (
    <span
      role={label ? 'img' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      className="absolute -right-1.5 -top-1 h-2 w-2 rounded-full bg-accent ring-2 ring-[#0e0e10]"
    />
  );
}

/**
 * Навигация телефона (≤ 900 px, решение владельца 2026-10-23 вместо гамбургера и выдвижного меню): сверху — логотип и
 * жетоны, внизу — панель вкладок «Главная · Слова и модули · Грамматика · Игра · Ещё»; «Ещё» поднимает лист над панелью:
 * «Полезное», «Сообщество», уведомления, настройки, тарифы, подарок за подписку, установка, админка. Панель — строка
 * сетки оболочки под `main` (Application): липкие низы страниц и строка создания на «Словах и модулях» встают над ней
 * сами. Тренировка и игра — вне оболочки, там панели нет. С открытой клавиатурой панели тоже нет (`tabBar`,
 * useSoftKeyboardOpen). Вкладок пять (с «Грамматикой», 2026-10-23), ширина равная, подпись — до двух строк: «Слова и модули»
 * на 320 px иначе не влезает.
 */
export function MobileNav({ tabBar }: { tabBar: boolean }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const user = useAuthStore((s) => s.user);
  const unreadCount = useNotificationsStore((s) => s.unreadCount);
  const moreOpen = useSidebarStore((s) => s.moreOpen);
  const setMoreOpen = useSidebarStore((s) => s.setMoreOpen);
  const pwa = usePwaInstall();
  const moreButton = useRef<HTMLButtonElement>(null);
  const sheet = useRef<HTMLDivElement>(null);
  const current = navSectionOf(pathname);

  // Страница модуля — своя верхняя строка (← назад, видимость, ⋯), тарифы — своя шапка с ✕ и липкая панель оплаты:
  // логотип и жетоны их не перекрывают. У подбора по теме между ними — «← Главная» (2026-10-16): подложки там нет.
  const onModuleDetail = !!useMatch(paths.unit);
  const onPlans = !!useMatch(paths.plans);
  const onTopicCards = !!useMatch(paths.topicCards);
  const ownAppBar = onModuleDetail || onPlans;
  const topBand = !ownAppBar && !onTopicCards;
  // «Установить на телефон» — пока приложение не стоит: открытое как приложение или уже установленное его не видит.
  const showInstall = !pwa.installed && (pwa.available || !installedBefore());

  const close = () => setMoreOpen(false);

  // Лист закрывается при переходе и не остаётся открытым, если окно стало шире телефона.
  useEffect(() => {
    setMoreOpen(false);
  }, [pathname, setMoreOpen]);
  useEffect(() => () => setMoreOpen(false), [setMoreOpen]);

  useEffect(() => {
    if (!moreOpen) return;
    sheet.current?.focus({ preventScroll: true });
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setMoreOpen(false);
      moreButton.current?.focus();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [moreOpen, setMoreOpen]);

  // Админ-пункт — только у роли ADMIN (выставляется вручную в БД); доступ всё равно проверяет бэкенд.
  const items: MoreItem[] = [
    { to: paths.useful, labelKey: 'nav.useful', Icon: LightbulbIcon },
    { to: paths.community, labelKey: 'nav.community', Icon: UsersIcon },
    { to: paths.notifications, labelKey: 'nav.notifications', Icon: BellIcon, badge: unreadCount },
    { to: paths.profile, labelKey: 'nav.profile', Icon: CogIcon },
    { to: paths.plans, labelKey: 'nav.plans', Icon: SparklesIcon },
    ...(user?.role === 'ADMIN' ? [{ to: paths.admin, labelKey: 'nav.admin', Icon: ShieldIcon }] : []),
  ];
  const rowClass = 'flex min-h-12 w-full items-center gap-3 rounded-[12px] px-3 text-left text-[15px] font-medium transition-colors';

  // Ведём на страницу установки, как лендинг: там «Установить» ставит приложение одним касанием (Android, Chrome), а на
  // iPhone — шаги. Системное окно отсюда не вызываем: beforeinstallprompt срабатывает один раз.
  const installApp = () => {
    close();
    navigate(`${paths.install}?from=app`);
  };

  return (
    <>
      {/* Подложка верха (приёмка A1-16, B16): полоса 56 px цвета фона с растворением 12 px снизу — прокрученный текст
          уходит под неё. z-30, как у логотипа и жетонов (они позже в DOM — сверху); на «Словах и модулях» она под строкой
          создания (z-45) и её затемнением. */}
      {topBand && (
        <div aria-hidden className="fixed inset-x-0 top-0 z-30 h-14 bg-base">
          <div className="pointer-events-none absolute inset-x-0 top-full h-3 bg-gradient-to-b from-base to-transparent" />
        </div>
      )}
      {topBand && (
        <Link to={paths.dashboard} aria-label={t('nav.aria.home')} className="fixed left-3 top-1.5 z-30 flex h-11 items-center rounded-xl px-1">
          <Logo size="sm" />
        </Link>
      )}
      {!ownAppBar && <TokenBalance variant="chip" />}

      {tabBar && (
        <nav
          aria-label={t('nav.aria.tabs')}
          className="relative z-40 border-t border-solid border-line bg-[#0e0e10]"
          style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
        >
          <div className="mx-auto flex max-w-[640px] items-stretch" style={{ height: TAB_BAR_HEIGHT }}>
            {TABS.map(({ section, to, labelKey, Icon, isNew }) => {
              const active = !moreOpen && current === section;
              return (
                <Link
                  key={section}
                  to={to}
                  data-tour={section}
                  aria-current={active ? 'page' : undefined}
                  onClick={close}
                  className={cn(TAB_CLASS, active ? 'text-accent' : 'text-muted hover:text-fg')}
                >
                  <span className="relative">
                    <Icon className="h-6 w-6" aria-hidden />
                    {isNew && <Dot label={t('nav.newBadge')} />}
                  </span>
                  <span className="line-clamp-2 max-w-full">{t(labelKey)}</span>
                </Link>
              );
            })}
            <button
              ref={moreButton}
              type="button"
              onClick={() => setMoreOpen(!moreOpen)}
              aria-expanded={moreOpen}
              aria-haspopup="dialog"
              aria-label={unreadCount > 0 ? t('nav.aria.moreUnread', { count: unreadCount }) : undefined}
              className={cn(TAB_CLASS, moreOpen || current === 'more' ? 'text-accent' : 'text-muted hover:text-fg')}
            >
              <span className="relative">
                <MoreIcon className="h-6 w-6" aria-hidden />
                {/* Уведомления — в «Ещё»: новые видно точкой на вкладке. */}
                {unreadCount > 0 && <Dot />}
              </span>
              <span className="line-clamp-2 max-w-full">{t('nav.more')}</span>
            </button>
          </div>
        </nav>
      )}

      {/* Лист «Ещё» — над панелью вкладок: она остаётся видна и нажимается («Ещё» ещё раз — закрыть). Обёртка
          обрезает лист снизу, поэтому он выезжает из-за панели, а не поверх неё. */}
      <AnimatePresence>
        {moreOpen && (
          <div key="more" className="fixed inset-x-0 top-0 z-[200] overflow-hidden" style={{ bottom: ABOVE_TAB_BAR }}>
            <motion.div
              aria-hidden
              className="absolute inset-0 bg-black/60"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={close}
            />
            <motion.div
              ref={sheet}
              role="dialog"
              aria-modal="true"
              aria-label={t('nav.more')}
              tabIndex={-1}
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ duration: 0.28, ease: [0.2, 0.9, 0.2, 1] }}
              className="absolute inset-x-0 bottom-0 mx-auto max-h-full w-full max-w-[640px] overflow-y-auto overscroll-contain rounded-t-[22px] border border-b-0 border-solid border-line bg-[#0e0e10] px-3 pb-3 pt-2 outline-none"
            >
              <div aria-hidden className="mx-auto mb-3 h-1 w-10 rounded-full bg-white/15" />
              {/* amp-mask / ym-hide-content: имя, email и инициалы не попадают в записи сессий. */}
              <div className="flex items-center gap-3 px-3 pb-3">
                <UserAvatar initials={user?.initials ?? '·'} className="amp-mask ym-hide-content" />
                <span className="amp-mask ym-hide-content min-w-0 flex-1">
                  <span className="block truncate text-[15px] font-medium text-fg">{user?.name || user?.email || '—'}</span>
                  {user?.name && <span className="block truncate text-[13px] text-muted">{user.email}</span>}
                </span>
              </div>
              <div className="flex flex-col gap-0.5 border-t border-solid border-line pt-2">
                {items.map(({ to, labelKey, Icon, badge }) => {
                  const active = !!matchPath({ path: to, end: false }, pathname);
                  return (
                    <Link
                      key={to}
                      to={to}
                      onClick={close}
                      aria-current={active ? 'page' : undefined}
                      className={cn(rowClass, active ? 'bg-elev text-fg' : 'text-fg hover:bg-[#17171a]')}
                    >
                      <Icon className="h-5 w-5 shrink-0 text-muted" aria-hidden />
                      <span className="min-w-0 flex-1 truncate">{t(labelKey)}</span>
                      {!!badge && badge > 0 && (
                        <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-pill bg-accent px-1.5 text-[12px] font-semibold leading-none text-fg-inv">
                          {badge > 99 ? '99+' : badge}
                        </span>
                      )}
                    </Link>
                  );
                })}
                {showInstall && (
                  <button type="button" onClick={installApp} className={cn(rowClass, 'text-fg hover:bg-[#17171a]')}>
                    <PhoneIcon className="h-5 w-5 shrink-0 text-muted" aria-hidden />
                    <span className="min-w-0 flex-1 truncate">{t('nav.installApp')}</span>
                  </button>
                )}
              </div>
              {/* Подарок за подписку — своей рамкой внизу; лист закрывается, открывается окно подарка. */}
              <div className="pt-2 [&>button]:mb-0">
                <SocialRewardSidebarRow collapsed={false} onOpen={close} />
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
}
